import React from 'react';
import { useIntl } from 'react-intl';

import { green } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { formatValue } from '@ts-core/utils/dataTableUtils';
import { getTimeComponents } from '@ts-core/utils/getTimeString';

interface TimeUnitProps {
  value: React.ReactNode;
  unit: string;
}

const TimeUnit = (props: TimeUnitProps) => {
  const { value, unit } = props;
  return (
    <Grid item xs={4}>
      <Typography align="center" color="textSecondary" fontSize={60} sx={{ opacity: 0.5 }}>
        {String(value).padStart(2, '0')}
      </Typography>
      <Typography align="center" color="textSecondary" fontSize={14} mt={-2}>
        {unit}
      </Typography>
    </Grid>
  );
};

export interface ResultCardProps {
  score?: number;
  maxScore?: number;
  usePercentage?: boolean;
  time?: number;
}

const ResultCard = (props: ResultCardProps) => {
  const { score, maxScore, time, usePercentage = true } = props;
  const intl = useIntl();

  if (score === undefined || !maxScore) {
    return null;
  }

  const percentageScore = (score / maxScore) * 100;
  const { hours, minutes, seconds } = getTimeComponents(time);

  return (
    <Box position="relative" width={{ xs: '100%', md: 600 }} mb={2}>
      <Box
        position="absolute"
        bgcolor="white"
        sx={{
          boxShadow: '0 0 10px  rgba(0,0,0,0.6)',
          top: 0,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: 64,
          width: 64,
          height: 64,
        }}
      >
        <CheckCircleIcon
          sx={{
            color: green[500],
            fontSize: 64,
            marginTop: '50%',
            marginLeft: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" align="center" mt={2}>
          {intl.formatMessage({ id: 'results' })}
        </Typography>
        <Box mt={1} height={24} sx={{ boxShadow: '0 0 10px  rgba(0,0,0,0.15)', border: 'solid 1px white', borderRadius: 12 }}>
          <Box
            width="100%"
            height={22}
            sx={{
              background: green[500],
              borderRadius: 11,
              clipPath: `inset(0px ${100 - percentageScore}% 0px 0px)`,
            }}
          />
        </Box>
        <Grid container style={{ marginTop: 16 }} spacing={1}>
          <Grid item xs={12} md={time ? 6 : 12}>
            <Typography variant="subtitle1" align="center" color="textSecondary" mb={-2}>
              {intl.formatMessage({ id: 'score' })}
            </Typography>
            <Typography align="center" color="textSecondary" fontSize={60} sx={{ opacity: 0.5 }}>
              {formatValue(score, maxScore, usePercentage)}
            </Typography>
          </Grid>
          {time && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" align="center" color="textSecondary" mb={-2}>
                {intl.formatMessage({ id: 'time' })}
              </Typography>
              <Grid container spacing={1} style={{ maxWidth: 280, margin: 'auto' }}>
                <TimeUnit value={hours} unit={intl.formatMessage({ id: 'hours' }, { value: 0 })} />
                <TimeUnit value={minutes} unit={intl.formatMessage({ id: 'minutes' }, { value: 0 })} />
                <TimeUnit value={seconds} unit={intl.formatMessage({ id: 'seconds' }, { value: 0 })} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ResultCard;
